import {
  AUTH_OPERATION_NAMES,
  EXPIRY_SKEW_SECONDS,
  GRAPHQL_URL,
  STORAGE_KEYS,
} from './config';

export interface AuthSession {
  authToken: string;
  /** Unix seconds. WPGraphQL returns these as strings or numbers depending on field. */
  authTokenExpiration: number;
  refreshToken: string;
  refreshTokenExpiration: number;
}

type Listener = () => void;

/**
 * Access token is held in memory only: it is short-lived (300s on this install) and
 * keeping it out of storage shrinks the XSS window. The refresh token has to persist
 * for "stay signed in" to mean anything, so it goes to localStorage — a deliberate
 * trade-off documented in _plans/customer-accounts-login-orders.md §2.1. Moving it
 * behind an httpOnly cookie is Phase 2b and only touches this file.
 */
let authToken: string | null = null;
let authTokenExpiration = 0;

let refreshPromise: Promise<string | null> | null = null;
let onUnauthenticated: (() => void) | null = null;
const listeners = new Set<Listener>();

const isBrowser = (): boolean => typeof window !== 'undefined';

const notify = (): void => {
  listeners.forEach(listener => listener());
};

/** Every storage read is wrapped: private mode, quota, and hand-edited values. */
const readStorage = (key: string): string | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.error(`Could not read ${key} from localStorage:`, error);

    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.error(`Could not write ${key} to localStorage:`, error);
  }
};

const removeStorage = (key: string): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error(`Could not remove ${key} from localStorage:`, error);
  }
};

export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

/** Called when a session cannot be recovered, so AuthProvider can drop to ANONYMOUS. */
export const setUnauthenticatedHandler = (handler: (() => void) | null): void => {
  onUnauthenticated = handler;
};

export const getAuthToken = (): string | null => authToken;

export const getRefreshToken = (): string | null =>
  readStorage(STORAGE_KEYS.REFRESH_TOKEN);

const nowSeconds = (): number => Math.floor(Date.now() / 1000);

export const isAuthTokenExpired = (): boolean =>
  !authToken || authTokenExpiration - EXPIRY_SKEW_SECONDS <= nowSeconds();

/**
 * The hint records only that a session existed and until when — never a token. A
 * corrupted or unparseable value is treated as "no hint" rather than throwing.
 */
export const readAuthHint = (): { exp: number } | null => {
  const raw = readStorage(STORAGE_KEYS.AUTH_HINT);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed.exp !== 'number') {
      return null;
    }

    return parsed.exp > nowSeconds() ? parsed : null;
  } catch {
    return null;
  }
};

export const hasRecoverableSession = (): boolean =>
  Boolean(readAuthHint() && getRefreshToken());

export const setSession = (session: AuthSession): void => {
  authToken = session.authToken;
  authTokenExpiration = Number(session.authTokenExpiration) || 0;

  writeStorage(STORAGE_KEYS.REFRESH_TOKEN, session.refreshToken);
  writeStorage(
    STORAGE_KEYS.AUTH_HINT,
    JSON.stringify({ exp: Number(session.refreshTokenExpiration) || 0 })
  );

  notify();
};

/** Updates just the access token, as returned by a refresh. */
export const setAuthToken = (token: string, expiration: number | string): void => {
  authToken = token;
  authTokenExpiration = Number(expiration) || 0;
};

export const clearSession = (): void => {
  authToken = null;
  authTokenExpiration = 0;
  refreshPromise = null;

  removeStorage(STORAGE_KEYS.REFRESH_TOKEN);
  removeStorage(STORAGE_KEYS.AUTH_HINT);
  // The Woo session identifies the *customer's* cart. Leaving it behind would hand the
  // next person on a shared device the previous customer's basket.
  removeStorage(STORAGE_KEYS.WOO_SESSION);

  notify();
};

export const signOut = (): void => {
  clearSession();

  if (onUnauthenticated) {
    onUnauthenticated();
  }
};

const REFRESH_MUTATION = `
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(input: { refreshToken: $refreshToken }) {
      authToken
      authTokenExpiration
      success
    }
  }
`;

/**
 * Deliberately a bare fetch rather than an Apollo operation. Going through the client
 * would re-enter the link chain, so a failing refresh could trigger errorLink, which
 * would trigger another refresh. Using fetch makes that recursion structurally
 * impossible instead of relying on a guard.
 */
const requestRefresh = async (token: string): Promise<string | null> => {
  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: REFRESH_MUTATION,
      variables: { refreshToken: token },
      operationName: 'RefreshToken',
    }),
  });

  if (!response.ok) {
    return null;
  }

  const json = await response.json();
  const payload = json?.data?.refreshToken;

  if (json?.errors?.length || !payload?.success || !payload?.authToken) {
    return null;
  }

  setAuthToken(payload.authToken, payload.authTokenExpiration);

  return payload.authToken;
};

/**
 * Single-flight: concurrent 401s (or several tabs' worth of queries) share one refresh
 * rather than stampeding WordPress with N identical mutations.
 *
 * Resolves to the new access token, or null when the session is unrecoverable.
 */
export const refresh = (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const token = getRefreshToken();

  if (!token) {
    return Promise.resolve(null);
  }

  refreshPromise = requestRefresh(token)
    .catch(error => {
      console.error('Token refresh failed:', error);

      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const isAuthOperation = (operationName?: string | null): boolean =>
  Boolean(operationName && AUTH_OPERATION_NAMES.includes(operationName));

/** Test seam — resets module state between tests. */
export const __resetForTests = (): void => {
  authToken = null;
  authTokenExpiration = 0;
  refreshPromise = null;
  onUnauthenticated = null;
  listeners.clear();
};
